import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { apiEndpoints } from "../../../api/endpoints";
import { jobApi } from "../../../api/job";
import { workflowApi } from "../../../api/workflow";
import Navbar from "../../layout/Navbar";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import {
  startBatchStepsPolling,
  handleResetScreenshot,
} from "../../../utils/browserSessionHelpers";
import ScreenshotPreview from "../../sections/ScreenshotPreview";
import DataFixture from "../../sections/DataFixture";
import StepScreenshot from "../../sections/StepScreenshot";
import { v4 as uuidv4 } from "uuid";

const TEST_ACTIONS = [
  {
    value: "type",
    label: "Type Text",
    fields: [
      {
        name: "value",
        label: "Text to type",
        placeholder: "Enter text to type",
      },
      {
        name: "element",
        label: "Element to type into",
        placeholder: "Type name of input here",
      },
    ],
  },
  {
    value: "click",
    label: "Click Element",
    fields: [
      {
        name: "element",
        label: "Element to click",
        placeholder: "Type element to click",
      },
    ],
  },
  {
    value: "navigate",
    label: "Navigate to URL",
    fields: [
      { name: "value", label: "URL", placeholder: "Enter URL to navigate to" },
    ],
  },
  {
    value: "select",
    label: "Select Option",
    fields: [
      {
        name: "value",
        label: "Option to Select",
        placeholder: "Enter option text or value",
      },
      {
        name: "element",
        label: "Dropdown Element",
        placeholder: "Type name of dropdown",
      },
    ],
  },
  {
    value: "enter",
    label: "Press Enter",
    fields: [],
  },
  {
    value: "reload",
    label: "Reload Page",
    fields: [],
  },
];

function CreateWorkflow() {
  const [sessionWorkflows, setSessionWorkflows] = useState([]);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    currentStep: { 
      action: "type", 
      value: "", 
      element: "",
      exact: false, 
      secret: false,  // Add secret flag
      value_mode: "static" // Add value_mode
    },
    savedSteps: [],
    editingStepIndex: undefined,
  });

  const { suiteId } = useParams();
  const navigate = useNavigate();

  const [sessionId, setSessionId] = useState(null);
  const [isPolling, setIsPolling] = useState(false);
  const [screenshots, setScreenshots] = useState([]);
  const [initialScreenshot, setInitialScreenshot] = useState(null);
  const [testStatus, setTestStatus] = useState(null);

  // Add useRef for the polling interval
  const pollIntervalRef = useRef(null);

  const [isFixtureModalOpen, setIsFixtureModalOpen] = useState(false);
  const [selectedFixture, setSelectedFixture] = useState(null);

  // Screenshot related state
  const [stepScreenshot, setStepScreenshot] = useState(null);
  const [screenshotPreview, setScreenshotPreview] = useState(null);
  const [isUploadingScreenshot, setIsUploadingScreenshot] = useState(false);

  useEffect(() => {
    const initializeBrowserSession = async () => {
      try {
        const result = await apiEndpoints.startBrowserSession(suiteId);
        setSessionId(result.sessionId);

        if (result.screenshot?.data) {
          setInitialScreenshot(`data:image/jpeg;base64,${result.screenshot.data}`);

        }
      } catch (error) {
        console.error("Failed to initialize browser session:", error);
        setError("Failed to start browser session: " + error.message);
      }
    };

    if (suiteId) {
      initializeBrowserSession();
    }
  }, [suiteId]);

  // Function to handle form changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  // Handler for the password checkbox
  const handleToggleSecret = () => {
    setFormData(prev => ({
      ...prev,
      currentStep: {
        ...prev.currentStep,
        secret: !prev.currentStep.secret
      }
    }));
  };

  // Handler for value mode selection
  const handleValueModeChange = (mode) => {
    setFormData(prev => ({
      ...prev,
      currentStep: {
        ...prev.currentStep,
        value_mode: mode
      }
    }));
  };

  const handleSaveStep = () => {
    const { action, value, element, exact, secret, value_mode } = formData.currentStep;

    // For click actions, respect the exact flag but don't modify the text
    const exactMatch = action === 'click' ? exact : false;
    
    // Helper function to extract text within brackets if needed
    const getSelector = (text, isExact) => {
      if (!text) return '';
      
      // If exact match is enabled, extract text within {{ }} brackets
      if (isExact) {
        const bracketMatch = text.match(/\{\{([^}]+)\}\}/);
        if (bracketMatch && bracketMatch[1]) {
          return bracketMatch[1].trim();
        }
      }
      
      // Otherwise return the full text
      return text;
    };
    
    // Log the state for debugging
    if (formData.editingStepIndex !== undefined) {
      if (action === "enter") {
        setFormData((prevState) => {
          const updatedSteps = [...prevState.savedSteps];
          updatedSteps[prevState.editingStepIndex] = {
            action: "enter",
            value: "",
            element: "",
            exact: false,
            secret: false, // Reset since not applicable for enter
            value_mode: "static", // Default value
            order: updatedSteps[prevState.editingStepIndex].order,
          };

          return {
            ...prevState,
            savedSteps: updatedSteps,
            currentStep: { action: "type", value: "", element: "", exact: false, secret: false, value_mode: "static" },
            editingStepIndex: undefined,
          };
        });
        return;
      }

      if (action === "reload") {
        setFormData((prevState) => {
          const updatedSteps = [...prevState.savedSteps];
          updatedSteps[prevState.editingStepIndex] = {
            action: "reload",
            value: "",
            element: "",
            exact: false,
            secret: false,
            value_mode: "static",
            order: updatedSteps[prevState.editingStepIndex].order,
          };

          return {
            ...prevState,
            savedSteps: updatedSteps,
            currentStep: { action: "type", value: "", element: "", exact: false, secret: false, value_mode: "static" },
            editingStepIndex: undefined,
          };
        });
        return;
      }

      // Handle screenshot URL if present
      let screenshotUrl = null;
      if (stepScreenshot) {
        // Check if stepScreenshot is a URL string or has uploadedUrl property
        screenshotUrl = typeof stepScreenshot === 'string' ? stepScreenshot : stepScreenshot?.uploadedUrl;
      }

      // Helper function to add screenshot metadata
      const addScreenshotMetadata = (stepData) => {
        if (screenshotUrl) {
          return {
            ...stepData,
            metadata: {
              ...stepData.metadata,
              step_screenshot: screenshotUrl
            }
          };
        }
        return stepData;
      };

      if (action === "navigate" && !value) {
        setError("Please enter a URL");
        return;
      }

      if (action !== "enter" && action !== "reload" && action !== "navigate" && !element) {
        setError("Please enter an element");
        return;
      }

      setFormData((prevState) => {
        const updatedSteps = [...prevState.savedSteps];
        
        if (action === "type") {
          // For type actions, include the secret flag and value_mode
          updatedSteps[prevState.editingStepIndex] = addScreenshotMetadata({
            action,
            value: value || "",
            element: element || "",
            exact: false, // Not applicable for type
            secret: secret, // Include secret flag
            value_mode: value_mode || "static", // Include value_mode
            order: updatedSteps[prevState.editingStepIndex].order,
          });
        } else if (action === "click") {
          // For click actions, include the exact flag
          updatedSteps[prevState.editingStepIndex] = addScreenshotMetadata({
            action,
            value: "",
            element: getSelector(element, exactMatch),
            exact: exactMatch,
            secret: false, // Not applicable for click
            value_mode: "static", // Default value
            order: updatedSteps[prevState.editingStepIndex].order,
          });
        } else if (action === "navigate") {
          // For navigate actions, include the value_mode
          updatedSteps[prevState.editingStepIndex] = addScreenshotMetadata({
            action,
            value: value || "",
            element: "",
            exact: false,
            secret: false,
            value_mode: value_mode || "static", // Include value_mode for navigate
            order: updatedSteps[prevState.editingStepIndex].order,
          });
        } else {
          // For other actions
          updatedSteps[prevState.editingStepIndex] = addScreenshotMetadata({
            action,
            value: value || "",
            element: element || "",
            exact: false, 
            secret: false, // Reset for other actions
            value_mode: "static", // Default value
            order: updatedSteps[prevState.editingStepIndex].order,
          });
        }

        return {
          ...prevState,
          savedSteps: updatedSteps,
          currentStep: { action: "type", value: "", element: "", exact: false, secret: false, value_mode: "static" },
          editingStepIndex: undefined,
        };
      });
      
      // Clear screenshot after saving
      setStepScreenshot(null);
      setScreenshotPreview(null);
      setError(null);
      return;
    }

    const nextOrder = formData.savedSteps.length + 1;

    // Handle screenshot URL if present
    let screenshotUrl = null;
    if (stepScreenshot) {
      // Check if stepScreenshot is a URL string or has uploadedUrl property
      screenshotUrl = typeof stepScreenshot === 'string' ? stepScreenshot : stepScreenshot?.uploadedUrl;
    }

    if (action === "enter") {
      setFormData((prevState) => ({
        ...prevState,
        savedSteps: [
          ...prevState.savedSteps,
          {
            id: uuidv4(),
            action: "enter",
            value: "",
            element: "",
            exact: false,
            secret: false,
            value_mode: "static", // Default value
            order: nextOrder,
            ...(screenshotUrl && { metadata: { step_screenshot: screenshotUrl } })
          },
        ],
        currentStep: { action: "type", value: "", element: "", exact: false, secret: false, value_mode: "static" },
      }));
      
      // Clear screenshot after saving
      setStepScreenshot(null);
      setScreenshotPreview(null);
      return;
    }

    if (action === "reload") {
      setFormData((prevState) => ({
        ...prevState,
        savedSteps: [
          ...prevState.savedSteps,
          {
            id: uuidv4(),
            action: "reload",
            value: "",
            element: "",
            exact: false,
            secret: false,
            value_mode: "static",
            order: nextOrder,
            ...(screenshotUrl && { metadata: { step_screenshot: screenshotUrl } })
          },
        ],
        currentStep: { action: "type", value: "", element: "", exact: false, secret: false, value_mode: "static" },
      }));
      
      // Clear screenshot after saving
      setStepScreenshot(null);
      setScreenshotPreview(null);
      return;
    }

    if (action === "navigate") {
      if (!value) {
        setError("Please enter a URL");
        return;
      }

      setFormData((prevState) => {
        // First, log the current value_mode
        console.log('Current value_mode before saving navigate step:', value_mode);
        
        return {
          ...prevState,
          savedSteps: [
            ...prevState.savedSteps,
            {
              id: uuidv4(),
              action,
              value,
              element: "",
              exact: false,
              secret: false,
              value_mode: value_mode || "static", // Ensure value_mode is set
              order: nextOrder,
              ...(screenshotUrl && { metadata: { step_screenshot: screenshotUrl } })
            },
          ],
          currentStep: { action: "type", value: "", element: "", exact: false, secret: false, value_mode: "static" },
        };
      });
      console.log('After saving navigate step, value_mode =', value_mode);
      
      // Clear screenshot after saving
      setStepScreenshot(null);
      setScreenshotPreview(null);
      return;
    }

    if (action === "click") {
      if (!element) {
        setError("Please specify an element to click");
        return;
      }

      setFormData((prevState) => ({
        ...prevState,
        savedSteps: [
          ...prevState.savedSteps,
          {
            id: uuidv4(),
            action,
            value: "",
            element: getSelector(element, exactMatch),
            exact: exactMatch,
            secret: false, // Not applicable for click
            value_mode: "static", // Default value
            order: nextOrder,
            ...(screenshotUrl && { metadata: { step_screenshot: screenshotUrl } })
          },
        ],
        currentStep: { action: "type", value: "", element: "", exact: false, secret: false, value_mode: "static" },
      }));
      
      // Clear screenshot after saving
      setStepScreenshot(null);
      setScreenshotPreview(null);
      return;
    }

    if (action === "type") {
      if (!element) {
        setError("Please provide an element to type into");
        return;
      }

      setFormData((prevState) => ({
        ...prevState,
        savedSteps: [
          ...prevState.savedSteps,
          {
            id: uuidv4(),
            action,
            value: value || "",
            element: element || "",
            exact: false,
            secret: secret, // Include secret flag for type actions
            value_mode: value_mode || "static", // Include value_mode for type actions
            order: nextOrder,
            ...(screenshotUrl && { metadata: { step_screenshot: screenshotUrl } })
          },
        ],
        currentStep: { action: "type", value: "", element: "", exact: false, secret: false, value_mode: "static" },
      }));
      
      // Clear screenshot after saving
      setStepScreenshot(null);
      setScreenshotPreview(null);
      return;
    }

    if (!action || !value || !element) {
      setError("Please fill in all fields for this step");
      return;
    }

    setFormData((prevState) => ({
      ...prevState,
      savedSteps: [
        ...prevState.savedSteps,
        {
          id: uuidv4(),
          action,
          value,
          element,
          exact: false,
          secret: false,
          value_mode: "static", // Default value
          order: nextOrder,
          ...(screenshotUrl && { metadata: { step_screenshot: screenshotUrl } })
        },
      ],
      currentStep: { action: "type", value: "", element: "", exact: false, secret: false, value_mode: "static" },
    }));

    // Clear screenshot after saving
    setStepScreenshot(null);
    setScreenshotPreview(null);
    setError(null);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSaveStep();
    }
  };

  const handleRemoveStep = (index) => {
    setFormData((prev) => ({
      ...prev,
      savedSteps: prev.savedSteps.filter((_, i) => i !== index),
    }));
  };

  const handleRemoveExistingScreenshot = () => {
    // Clear existing screenshot preview but keep the current step data
    setStepScreenshot(null);
    setScreenshotPreview(null);
  };

  const handleEditStep = (index) => {
    // Get the step we're editing
    const stepToEdit = { ...formData.savedSteps[index] };
    
    // For click actions, check if the existing step has the exact property
    if (stepToEdit.action === 'click') {
      // Make sure the exact property is properly set
    }
    
    // Extract screenshot from metadata if it exists
    if (stepToEdit.metadata?.step_screenshot) {
      setStepScreenshot(stepToEdit.metadata.step_screenshot);
      setScreenshotPreview(stepToEdit.metadata.step_screenshot);
    } else {
      setStepScreenshot(null);
      setScreenshotPreview(null);
    }
    
    // Set the current step to the selected saved step
    setFormData(prev => ({
      ...prev,
      currentStep: stepToEdit,
      editingStepIndex: index,
    }));
  };

  // Helper function to format step names for display
  const formatStepNameForDisplay = (step) => {
    switch (step.action) {
      case "type":
        // Mask the value if the element is a password field or if secret is true
        const shouldMaskValue = step.element?.toLowerCase().includes('password') || step.secret === true;
        const displayValue = shouldMaskValue ? '••••••••' : step.value;
        
        // Add value mode indicator
        let valueModeIndicator = "";
        if (step.value_mode === "fixture") {
          valueModeIndicator = " (fixture)";
        } else if (step.value_mode === "random") {
          valueModeIndicator = " (random)";
        }
        
        return `Type "${displayValue}" into ${step.element}`;
      case "click":
        return `Click on ${step.element}`;
      case "navigate":
        let navModeIndicator = "";
        if (step.value_mode === "fixture") {
          navModeIndicator = " (fixture)";
        }
        return `Navigate to ${step.value}${navModeIndicator}`;
      case "select":
        return `Select "${step.value}" from "${step.element}"`;
      case "enter":
        return `Press Enter key`;
      case "reload":
        return `Reload page`;
      default:
        return `${step.action} ${step.element}`;
    }
  };

  // Original formatStepName function for API use (no masking)
  const formatStepName = (step) => {
    switch (step.action) {
      case "type":
        let valueModeIndicator = "";
        if (step.value_mode === "fixture") {
          valueModeIndicator = " (fixture)";
        } else if (step.value_mode === "random") {
          valueModeIndicator = " (random)";
        }
        return `Type "${step.value}" into ${step.element}`;
      case "click":
        return `Click on ${step.element}`;
      case "navigate":
        let navModeIndicator = "";
        if (step.value_mode === "fixture") {
          navModeIndicator = " (fixture)";
        }
        return `Navigate to ${step.value}${navModeIndicator}`;
      case "select":
        return `Select "${step.value}" from "${step.element}"`;
      case "enter":
        return `Press Enter key`;
      case "reload":
        return `Reload page`;
      default:
        return `${step.action} ${step.element}`;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      alert("Please provide a workflow name");
      return;
    }

    if (formData.savedSteps.length === 0) {
      alert("Please add at least one step");
      return;
    }

    try {
      // Helper function to extract text within brackets if needed
      const getSelector = (text, isExact) => {
        if (!text) return '';
        
        // If exact match is enabled, extract text within {{ }} brackets
        if (isExact) {
          const bracketMatch = text.match(/\{\{([^}]+)\}\}/);
          if (bracketMatch && bracketMatch[1]) {
            return bracketMatch[1].trim();
          }
        }
        
        // Otherwise return the full text
        return text;
      };
      
      const formattedSteps = formData.savedSteps.map((step) => {
        // Process the selector based on action type and exact flag
        const selector = step.action === 'click' 
          ? getSelector(step.element, step.exact) 
          : (step.element || '');
          
        const formattedStep = {
          order: step.order,
          existingStepId: null,
          newStepData: {
            name: formatStepName(step),
            metadata: {
              actionType: step.action,
              selector: selector,
              value: step.value,
              ...(step.action === "click" && { exact: step.exact }),
              ...(step.action === "type" && { 
                secret: step.secret,
                value_mode: step.value_mode || "static"
              }),
              ...(step.action === "navigate" && { 
                value_mode: step.value_mode || "static"
              }),
              ...(step.metadata?.step_screenshot && { step_screenshot: step.metadata.step_screenshot })
            },
          },
          subWorkflowId: null,
        };

        return formattedStep;
      });

      const workflowData = {
        name: formData.name,
        description: formData.description || "No description provided",
        workflow_type: formData.workflow_type || "regular", // Add default if missing
        steps: formattedSteps,
      };

      const response = await workflowApi.createWorkflow(suiteId, workflowData);
      setSessionWorkflows((prevWorkflows) => [...prevWorkflows, response]);

      // Reset form and navigate back
      setFormData({
        name: "",
        description: "",
        workflow_type: "regular", // Reset to default
        currentStep: { action: "type", value: "", element: "", exact: false, secret: false, value_mode: "static" },
        savedSteps: [],
        editingStepIndex: undefined,
      });

      navigate(`/testsuites/${suiteId}/workflows`);
    } catch (error) {
      console.error("Error creating workflow:", error);
      setError(
        "Failed to create workflow: " + (error.message || "Unknown error")
      );
    }
  };

  const handleResetScreenshotWrapper = async () => {
    await handleResetScreenshot(suiteId, {
      pollIntervalRef,
      setIsPolling,
      setTestStatus,
      setSessionId,
      setScreenshots,
      setInitialScreenshot,
      setError,
    });
  };

  const handleRunFromStart = async () => {
    try {
      setIsPolling(true);
      setError(null);

      // Execute the steps from the beginning
      const response = await apiEndpoints.executeBrowserSteps(
        formData.savedSteps,
        suiteId,
        sessionId
      );

      // Use the helper function instead of startPolling
      startBatchStepsPolling(sessionId, {
        setIsPolling,
        setTestStatus,
        setScreenshots,
        setError,
        pollIntervalRef,
      });
    } catch (error) {
      console.error("Error running workflow steps:", error);
      setError("Failed to run workflow steps: " + error.message);
      setIsPolling(false);
    }
  };

  const handleRunAllSteps = async () => {
    if (isPolling) {
      try {
        await apiEndpoints.cancelBrowserSession(sessionId);
        
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
        }
        
        setIsPolling(false);
        
        setTestStatus({
          status: "completed",
          description: "Test execution cancelled by user"
        });
        
      } catch (error) {
        console.error("Error cancelling test execution:", error);
        setError(`Failed to cancel test execution: ${error.message}`);
      }
      return;
    }

    openFixtureModal();
  };

  const openFixtureModal = () => {
    setIsFixtureModalOpen(true);
  };

  const closeFixtureModal = () => {
    setIsFixtureModalOpen(false);
  };

  const handleFixtureSelect = (fixture) => {
    setSelectedFixture(fixture);
    runSteps(fixture?.id);
  };

  const handleRunWithoutFixture = () => {
    setSelectedFixture(null);
    runSteps();
  };

  // New function to handle the actual running of steps
  const runSteps = async (fixtureId = null) => {
    try {
      if (!sessionId) {
        setError("No active browser session");
        return;
      }

      if (formData.savedSteps.length === 0) {
        setError("Please add at least one step first");
        return;
      }

      setIsPolling(true);
      setTestStatus(null);

      // Format the steps before sending them
      const formattedSteps = formData.savedSteps.map(step => {
        if (step.action === 'workflow') {
          return {
            action: step.action,
            workflowId: step.workflowId
          };
        }

        if (step.action === 'click') {
          let elementValue = step.element || '';
          
          if (step.exact) {
            const bracketMatch = elementValue.match(/\{\{([^}]+)\}\}/);
            if (bracketMatch && bracketMatch[1]) {
              elementValue = bracketMatch[1].trim();
            }
          }
          
          return {
            action: step.action,
            element: elementValue || undefined,
            ...(step.metadata?.step_screenshot && { step_screenshot: step.metadata.step_screenshot })
            
          };
        }

        if (step.action === 'type') {
          return {
            action: step.action,
            element: step.element || undefined,
            value: step.value || undefined,
            secret: step.secret || false,
            value_mode: step.value_mode || 'static',
            ...(step.metadata?.step_screenshot && { step_screenshot: step.metadata.step_screenshot })

          };
        }

        if (step.action === 'navigate') {
          console.log('Navigate step value_mode:', step.value_mode);
          return {
            action: step.action,
            value: step.value || undefined,
            value_mode: step.value_mode || "static"
          };
        }

        return {
          action: step.action,
          element: step.element || undefined,
          value: step.value || undefined,
          ...(step.metadata?.step_screenshot && { step_screenshot: step.metadata.step_screenshot })

        };
      });

      await apiEndpoints.executeBrowserSteps(
        formattedSteps,
        suiteId,
        sessionId,
        fixtureId
      );
      
      startBatchStepsPolling();
    } catch (error) {
      console.error('Error running steps:', error);
      setError(`Failed to run steps: ${error.message}`);
      setIsPolling(false);
    }
  };

  // For batch steps polling
  const startBatchStepsPolling = () => {
    if (isPolling) return;
  
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  
    setIsPolling(true);
    setScreenshots([]); 

    // Store the current step index to track progress
    let currentStepIndex = 0;
  
    pollIntervalRef.current = setInterval(async () => {
      try {
        const result = await apiEndpoints.getBatchStepsResult(sessionId);
        const { status, screenshot, current_step } = result;
  
        if (
          status === "completed" ||
          status === "error" ||
          status === "failed"
        ) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
          setIsPolling(false);
          setTestStatus({
            status,
            description:
              status === "error"
                ? result.error || "Steps failed"
                : "All steps completed successfully",
          });
  
          if (screenshot?.data) {
            // Add final screenshot if it's new
            if (current_step > currentStepIndex) {
              setScreenshots((prev) => [...prev, `data:image/jpeg;base64,${screenshot.data}`]);
            } else {
              // Update the last screenshot
              setScreenshots((prev) => {
                const newScreenshots = [...prev];
                if (newScreenshots.length > 0) {
                  newScreenshots[newScreenshots.length - 1] = `data:image/jpeg;base64,${screenshot.data}`;
                } else {
                  newScreenshots.push(`data:image/jpeg;base64,${screenshot.data}`);
                }
                return newScreenshots;
              });
            }
          }
        } else if (status === "running" && screenshot?.data) {
          // Check if we've moved to a new step
          if (current_step && current_step > currentStepIndex) {
            // We're on a new step, add a new screenshot
            setScreenshots((prev) => [...prev, `data:image/jpeg;base64,${screenshot.data}`]);
            currentStepIndex = current_step;
          } else {
            // We're still on the same step, update the last screenshot
            setScreenshots((prev) => {
              const newScreenshots = [...prev];
              if (newScreenshots.length > 0) {
                newScreenshots[newScreenshots.length - 1] = `data:image/jpeg;base64,${screenshot.data}`;
              } else {
                newScreenshots.push(`data:image/jpeg;base64,${screenshot.data}`);
              }
              return newScreenshots;
            });
          }
        }
      } catch (error) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
        setIsPolling(false);
        setError("Failed to get batch steps result: " + error.message);
      }
    }, 3000);
  };

  // Clean up interval on component unmount
  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, []);

  // Add this function to handle drag end events
  const handleDragEnd = (result) => {
    // If dropped outside the list or no movement, do nothing
    if (!result.destination || result.destination.index === result.source.index) {
      return;
    }

    // Create a copy of the saved steps
    const newSteps = Array.from(formData.savedSteps);
    
    // Remove the dragged item from its original position
    const [movedItem] = newSteps.splice(result.source.index, 1);
    
    // Insert the dragged item at its new position
    newSteps.splice(result.destination.index, 0, movedItem);
    
    // Update the order property for each step
    const reorderedSteps = newSteps.map((step, index) => ({
      ...step,
      order: index + 1
    }));
    
    // Update the state with the new order
    setFormData(prev => ({
      ...prev,
      savedSteps: reorderedSteps
    }));
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="container mx-auto p-4">
        <div className="flex items-center gap-2 mb-6">
          <Link
            to={`/testsuites/${suiteId}/workflows`}
            className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg transition-colors flex items-center gap-2"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 19l-7-7m0 0l7-7m-7 7h18"
              />
            </svg>
            Back to Workflows
          </Link>
        </div>

        <div className="grid grid-cols-2 gap-8">
          <div>
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Workflow Name
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:border-black"
                  placeholder="Enter workflow name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:border-black"
                  placeholder="Enter workflow description"
                  rows="3"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Workflow Type
                </label>
                <select
                  name="workflow_type"
                  value={formData.workflow_type || "regular"}
                  onChange={handleChange}
                  className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:border-black"
                >
                  <option value="regular">Regular Workflow</option>
                  <option value="post_execution">
                    Post-Execution (Cleanup)
                  </option>
                </select>
              </div>

              {formData.savedSteps.length > 0 && (
                <div className="border rounded-lg overflow-hidden">
                  <div className="bg-gray-50 px-4 py-3 border-b">
                    <h3 className="text-sm font-medium text-gray-700">
                      Saved Steps
                    </h3>
                  </div>
                  <DragDropContext onDragEnd={handleDragEnd}>
                    <Droppable droppableId="steps-list">
                      {(provided) => (
                        <div 
                          className="divide-y" 
                          ref={provided.innerRef}
                          {...provided.droppableProps}
                        >
                          {formData.savedSteps.map((step, index) => (
                            <Draggable 
                              key={`step-${index}`} 
                              draggableId={`step-${index}`} 
                              index={index}
                            >
                              {(provided, snapshot) => (
                                <div 
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  className={`px-4 py-3 flex items-center justify-between hover:bg-gray-50 ${
                                    snapshot.isDragging ? 'bg-blue-50' : ''
                                  }`}
                                >
                                  <div className="flex-1 flex items-center">
                                    {/* Drag handle */}
                                    <div 
                                      {...provided.dragHandleProps}
                                      className="mr-2 cursor-grab text-gray-400 hover:text-gray-600"
                                    >
                                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
                                      </svg>
                                    </div>
                                    
                                    <div className="flex-1">
                                      <div className="flex items-center gap-2">
                                        <span className="text-xs font-medium text-gray-500">
                                          {index + 1}.
                                        </span>
                                        <span className="text-sm font-medium text-gray-900">
                                          {formatStepNameForDisplay(step)}
                                        </span>
                                      </div>
                                      
                                      {step.action === "type" && step.secret && (
                                        <div className="flex items-center mt-1 text-xs text-blue-600">
                                          <svg className="w-3 h-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                          </svg>
                                          Password field (value masked)
                                        </div>
                                      )}
                                      {step.action === "navigate" && step.value_mode === "fixture" && (
                                        <div className="flex items-center mt-1 text-xs text-blue-600">
                                          <span className="ml-2 text-xs bg-blue-100 text-blue-800 py-0.5 px-1.5 rounded">
                                            fixture
                                          </span>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                  <div className="flex items-center">
                                    <button
                                      type="button"
                                      onClick={() => handleEditStep(index)}
                                      className="text-blue-600 hover:text-blue-900 mr-3"
                                    >
                                      Edit
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => handleRemoveStep(index)}
                                      className="text-red-600 hover:text-red-900"
                                    >
                                      Remove
                                    </button>
                                  </div>
                                </div>
                              )}
                            </Draggable>
                          ))}
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </DragDropContext>
                </div>
              )}

              <div>
                <h2 className="text-lg font-medium text-gray-900 mb-4">
                  Add New Step
                </h2>

                <div className="bg-gray-50 p-4 rounded-lg mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Action
                    </label>
                    <select
                      value={formData.currentStep.action}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          currentStep: {
                            ...prev.currentStep,
                            action: e.target.value,
                            // Reset appropriate flags based on action type
                            exact: e.target.value === 'click' ? prev.currentStep.exact : false,
                            secret: e.target.value === 'type' ? prev.currentStep.secret : false,
                            // Preserve value_mode for navigate and type actions
                            value_mode: (e.target.value === 'navigate' || e.target.value === 'type') 
                              ? prev.currentStep.value_mode 
                              : 'static'
                          },
                        }))
                      }
                      className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:border-black"
                    >
                      {TEST_ACTIONS.map((action) => (
                        <option key={action.value} value={action.value}>
                          {action.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {TEST_ACTIONS.find(
                    (a) => a.value === formData.currentStep.action
                  )?.fields.map((field) => (
                    <div key={field.name}>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {field.label}
                      </label>
                      <div className="flex">
                        <input
                          type={formData.currentStep.secret && field.name === 'value' && formData.currentStep.action === 'type' ? "password" : "text"}
                          value={formData.currentStep[field.name]}
                          onChange={(e) => {
                            const newValue = e.target.value;
                            
                            if (formData.currentStep.action === 'click' && field.name === 'element') {
                              const hasExactBrackets = newValue && /^\{\{.*\}\}$/.test(newValue.trim());
                              setFormData(prev => ({
                                ...prev,
                                currentStep: { 
                                  ...prev.currentStep, 
                                  [field.name]: newValue,
                                  exact: hasExactBrackets
                                }
                              }));
                            } else if (formData.currentStep.action === 'navigate' && field.name === 'value') {
                              // Check if value is in {fixture} format and auto-set value_mode to fixture
                              const isFixtureFormat = newValue && /^\{[^{}]+\}$/.test(newValue.trim());
                              
                              setFormData(prev => ({
                                ...prev,
                                currentStep: { 
                                  ...prev.currentStep, 
                                  [field.name]: newValue,
                                  value_mode: isFixtureFormat ? 'fixture' : prev.currentStep.value_mode
                                }
                              }));
                            } else {
                              // Default behavior for other fields
                              setFormData(prev => ({
                                ...prev,
                                currentStep: { ...prev.currentStep, [field.name]: newValue }
                              }));
                            }
                          }}
                          onKeyPress={handleKeyPress}
                          placeholder={field.placeholder}
                          className={`${
                            formData.currentStep.action === "click" && field.name === "element"
                              ? "rounded-l"
                              : formData.currentStep.action === "type" && field.name === "value"
                              ? "rounded-l"
                              : "rounded"
                          } w-full p-2 border border-gray-300 focus:border-black focus:outline-none`}
                        />
                        {formData.currentStep.action === "click" && field.name === "element" && (
                          <button
                            type="button"
                            onClick={() => {
                              // Simply toggle the exact property without modifying the text
                              setFormData((prev) => ({
                                ...prev,
                                currentStep: {
                                  ...prev.currentStep,
                                  exact: !prev.currentStep.exact
                                },
                              }));
                            }}
                            className={`${
                              formData.currentStep.exact
                                ? "bg-blue-600 hover:bg-blue-700"
                                : "bg-gray-400 hover:bg-gray-500"
                            } text-white px-3 py-2 rounded-r border-l-0 transition-colors`}
                            title={formData.currentStep.exact ? "Exact match enabled" : "Exact match disabled"}
                          >
                            Exact {formData.currentStep.exact ? 'ON' : 'OFF'}
                          </button>
                        )}
                        {formData.currentStep.action === "type" && field.name === "value" && (
                          <button
                            type="button"
                            onClick={handleToggleSecret}
                            className={`${
                              formData.currentStep.secret
                                ? "bg-purple-600 hover:bg-purple-700"
                                : "bg-gray-400 hover:bg-gray-500"
                            } text-white px-3 py-2 rounded-r border-l-0 transition-colors flex items-center`}
                            title={formData.currentStep.secret ? "Password field (value masked)" : "Regular text field"}
                          >
                            <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                            </svg>
                            {formData.currentStep.secret ? 'Password ON' : 'Password OFF'}
                          </button>
                        )}
                      </div>
                      {formData.currentStep.action === 'type' && field.name === 'element' && formData.currentStep.element?.toLowerCase().includes('password') && !formData.currentStep.secret && (
                        <div className="mt-1 text-xs text-amber-600 flex items-center">
                          <svg className="w-3 h-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                          </svg>
                          Element name contains "password". Consider enabling password masking.
                        </div>
                      )}
                    </div>
                  ))}

                  {formData.currentStep.action === "type" && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Value Mode
                      </label>
                      <div className="flex space-x-2">
                        <button
                          type="button"
                          onClick={() => handleValueModeChange("static")}
                          className={`px-3 py-2 rounded text-sm ${
                            formData.currentStep.value_mode === "static"
                              ? "bg-blue-600 text-white"
                              : "bg-gray-200 text-gray-800"
                          }`}
                        >
                          Static
                        </button>
                        <button
                          type="button"
                          onClick={() => handleValueModeChange("fixture")}
                          className={`px-3 py-2 rounded text-sm ${
                            formData.currentStep.value_mode === "fixture"
                              ? "bg-blue-600 text-white"
                              : "bg-gray-200 text-gray-800"
                          }`}
                        >
                          Fixture
                        </button>
                        <button
                          type="button"
                          onClick={() => handleValueModeChange("random")}
                          className={`px-3 py-2 rounded text-sm ${
                            formData.currentStep.value_mode === "random"
                              ? "bg-blue-600 text-white"
                              : "bg-gray-200 text-gray-800"
                          }`}
                        >
                          Random
                        </button>
                      </div>
                      {formData.currentStep.value_mode !== "static" && (
                        <div className="mt-1 text-xs text-blue-600">
                          {formData.currentStep.value_mode === "fixture" 
                            ? "The value will be treated as a fixture name."
                            : "A random value will be generated."
                          }
                        </div>
                      )}
                    </div>
                  )}

                  {formData.currentStep.action === "navigate" && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Value Mode
                      </label>
                      <div className="flex space-x-2">
                        <button
                          type="button"
                          onClick={() => handleValueModeChange("static")}
                          className={`px-3 py-2 rounded text-sm ${
                            formData.currentStep.value_mode === "static"
                              ? "bg-blue-600 text-white"
                              : "bg-gray-200 text-gray-800"
                          }`}
                        >
                          Static
                        </button>
                        <button
                          type="button"
                          onClick={() => handleValueModeChange("fixture")}
                          className={`px-3 py-2 rounded text-sm ${
                            formData.currentStep.value_mode === "fixture"
                              ? "bg-blue-600 text-white"
                              : "bg-gray-200 text-gray-800"
                          }`}
                        >
                          Fixture
                        </button>
                      </div>
                      {formData.currentStep.value_mode === "fixture" && (
                        <div className="mt-1 text-xs text-blue-600">
                          The URL value will be treated as a fixture name. Use format: {'{fixture_name}'}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Step Screenshot Component */}
                  {(formData.currentStep.action === "type" || 
                    formData.currentStep.action === "click" || 
                    formData.currentStep.action === "select") && (
                    <StepScreenshot
                      stepScreenshot={stepScreenshot}
                      setStepScreenshot={setStepScreenshot}
                      screenshotPreview={screenshotPreview}
                      setScreenshotPreview={setScreenshotPreview}
                      isUploadingScreenshot={isUploadingScreenshot}
                      setIsUploadingScreenshot={setIsUploadingScreenshot}
                      stepId={formData.editingStepIndex !== undefined ? 
                        (formData.savedSteps[formData.editingStepIndex]?.id || `temp-${Date.now()}`) : 
                        `temp-${Date.now()}`
                      }
                      existingScreenshotUrl={formData.currentStep?.metadata?.step_screenshot}
                      onRemoveExistingScreenshot={handleRemoveExistingScreenshot}
                    />
                  )}

                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={handleSaveStep}
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded transition-colors mb-2"
                    >
                      {formData.editingStepIndex !== undefined
                        ? "Update Step"
                        : "Save Step"}
                    </button>

                    {formData.editingStepIndex !== undefined && (
                      <button
                        type="button"
                        onClick={() => {
                          setFormData((prev) => ({
                            ...prev,
                            currentStep: {
                              action: "type",
                              value: "",
                              element: "",
                              exact: false,
                              secret: false,
                              value_mode: "static",
                            },
                            editingStepIndex: undefined,
                          }));
                        }}
                        className="w-full bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium py-2 px-4 rounded transition-colors"
                      >
                        Cancel Edit
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {error && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4">
                  {error}
                </div>
              )}

              <div className="flex justify-end">
                <button
                  onClick={handleSubmit}
                  className="bg-black hover:bg-gray-800 text-white font-bold py-2 px-4 rounded transition-colors"
                >
                  Create Workflow
                </button>
              </div>
            </div>
          </div>

          {/* Right Column - Screenshot */}
          <div className="bg-white rounded-lg shadow-md p-6">
          
            <div className="flex flex-col gap-4">
            <ScreenshotPreview
                title="Screenshot Preview"
                isPolling={isPolling}
                screenshots={[initialScreenshot,...screenshots]}
                testStatus={testStatus}
                pollingMessage="Running test case..."
                loadingMessage="Running test steps..."
                successMessage="All test steps completed successfully."
                onReset={handleResetScreenshotWrapper}
                onRun={handleRunAllSteps}
                runButtonDisabled={formData.savedSteps.length === 0}
                runButtonText={isPolling ? "Cancel" : "Run from start"}
                runButtonClass={isPolling ? "bg-red-600 hover:bg-red-700" : "bg-green-600 hover:bg-green-700"}    
              />
              </div>
          </div>
        </div>
      </div>

      <DataFixture 
        isOpen={isFixtureModalOpen}
        onClose={closeFixtureModal}
        onSelect={handleFixtureSelect}
        onRunWithoutFixture={handleRunWithoutFixture}
      />
    </div>
  );
}

export default CreateWorkflow;